import React, { useEffect, useMemo, useRef, useState } from 'react'
import { observer } from 'mobx-react'

import Script from 'next/script'

import { isProduction, isUat } from 'utils/env.utils'
import {
  MANGO_PAY_PROVIDER_DATA,
  MANGO_PAY_PROVIDER_DATA_UAT,
  MANGO_PAY_SCRIPT_SRC,
} from 'constants/pricingPlans.constants'
import { notify } from 'libs/common/notify'

import { MangoPayCardPayment } from '../../../../../interfaces/payment-method-interfaces/MangoPayCardPayment'

interface MangoPayCreditCardProps {
  updateCreditCardData: (params: { isValid: boolean }) => void
  isActive: boolean
  store: MangoPayCardPayment
}

const AblefyMangoPayCreditCard: React.FC<MangoPayCreditCardProps> = observer(
  ({ updateCreditCardData, isActive, store }) => {
    const [isReady, setIsReady] = useState<boolean>(false)
    const cardRef = useRef(null)

    const currency = store.currency
    const amount = store.totalPrice()

    const options = useMemo(
      () => ({
        ...(isUat() ? MANGO_PAY_PROVIDER_DATA_UAT : MANGO_PAY_PROVIDER_DATA),
        environment: isProduction() ? 'PRODUCTION' : 'SANDBOX',
        amount: {
          currency: currency.toUpperCase(),
          amount,
        },
        locale: I18n.locale,
        paymentMethod: {
          type: 'card',
          options: {
            enableSaveCard: false,
            supportedCardBrands: ['VISA', 'MASTERCARD', 'MAESTRO', 'CB'],
            onCreateCardRegistration: store.mangoPayOnCreateCardRegistration,
            onCreatePayment: store.mangoPayOnCreatePayment,
          },
        },
      }),
      []
    )

    useEffect(() => {
      if (isActive && isReady) {
        initForm()
      }
    }, [isActive, isReady])

    useEffect(
      () => () => {
        if (cardRef.current?.unmount) {
          cardRef.current.unmount()
        }
      },
      []
    )

    const initForm = async () => {
      try {
        const mangoCardForm = await (window as any).CheckoutSdk.loadCardFormElement('#mangopay-card-container', options)

        if (!mangoCardForm) {
          throw new Error('Failed to load MangopayCheckoutSdk')
        }

        cardRef.current = mangoCardForm

        mangoCardForm.on('error', (event: CustomEvent) => {
          const message = event.detail?.error?.ResultMessage
          if (message !== 'error.unknown' && message !== 'ablefy.error') {
            store.hideLoading()
            notify('error', event.detail?.error?.ResultMessage)
            console.error(event.detail)
          }
        })

        mangoCardForm.on('change', (event: CustomEvent) => {
          if (event?.detail.isComplete) {
            updateCreditCardData({ isValid: true })
          }
        })

        store.setMangoPayForm(mangoCardForm)
      } catch (error) {
        console.error('Failed to initialize MangoPay SDK:', error)
      }
    }

    const handleScriptLoad = () => {
      setIsReady(true)
    }

    return (
      <>
        <Script
          src={MANGO_PAY_SCRIPT_SRC}
          onLoad={handleScriptLoad}
          onError={(errr) => {
            console.error('Failed to load MangoPay SDK:', errr)
            notify('error', I18n.t('react.payer.common.use_another_paymethod'))
          }}
          onReady={() => setIsReady(true)}
          strategy='lazyOnload'
        />
        <div id='mangopay-card-container'></div>
      </>
    )
  }
)

export default AblefyMangoPayCreditCard
